import { OrgScopedBaseUser } from "@models";
import {
  Autocomplete,
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FunctionalGroup } from "@types";
import type { ReactNode } from "react";
import { SwitchButton } from "src/components/ui/SwitchButton";
import {
  TASK_PROGRAM_DUE_AFTER_DAYS_OPTIONS,
  TaskProgramDueAfterDays,
  TrainingCompletionMethod,
} from "src/services/backend";
import { getUserDisplayName } from "src/utils/user";

export type TaskProgramSettingsValues = {
  name: string;
  description: string;
  enabled: boolean;
  completionMethod: TrainingCompletionMethod;
  dueAfterDays: TaskProgramDueAfterDays | null;
};

export type TaskProgramAssignmentValues = {
  functionalGroupIds: string[];
  userIds: string[];
};

export const DUE_AFTER_DAYS_LABEL: Record<TaskProgramDueAfterDays, string> = {
  7: "7 days",
  14: "14 days",
  30: "30 days",
  180: "180 days",
  365: "365 days",
};

export const SELECTED_AUTOCOMPLETE_SX = {
  "&& .MuiAutocomplete-inputRoot": {
    paddingTop: "14px",
    paddingBottom: "6px",
  },
  "&& .MuiAutocomplete-tag": {
    marginTop: "4px",
  },
};

type TaskProgramSettingsFieldsProps = {
  linkedDocumentTitle: string;
  values: TaskProgramSettingsValues;
  onChange: (values: TaskProgramSettingsValues) => void;
  completionMethodHelperText: string;
  disabled?: boolean;
  enabledInputId?: string;
  children?: ReactNode;
};

export const TaskProgramSettingsFields = ({
  linkedDocumentTitle,
  values,
  onChange,
  completionMethodHelperText,
  disabled = false,
  enabledInputId = "training-enabled-switch",
  children,
}: TaskProgramSettingsFieldsProps) => (
  <Stack spacing={3}>
    <Typography variant="body2" color="text.secondary">
      Read-and-sign training is sent automatically when a new revision is
      published. Quiz-and-sign training is sent after that revision&apos;s quiz
      is published.
    </Typography>

    <TextField
      label="Linked document"
      value={linkedDocumentTitle}
      disabled
      fullWidth
      helperText="The linked document is fixed for the lifetime of a training."
    />

    <TextField
      label="Name"
      value={values.name}
      onChange={(event) => onChange({ ...values, name: event.target.value })}
      disabled={disabled}
      fullWidth
      required
    />

    <TextField
      label="Description (optional)"
      value={values.description}
      onChange={(event) =>
        onChange({ ...values, description: event.target.value })
      }
      disabled={disabled}
      fullWidth
      multiline
      minRows={2}
    />

    <TextField
      select
      label="Completion method"
      value={values.completionMethod}
      onChange={(event) =>
        onChange({
          ...values,
          completionMethod: event.target.value as TrainingCompletionMethod,
        })
      }
      helperText={completionMethodHelperText}
      disabled={disabled}
      fullWidth
    >
      <MenuItem value="ACKNOWLEDGEMENT">Read and sign</MenuItem>
      <MenuItem value="QUIZ">Quiz and sign</MenuItem>
    </TextField>

    {children}

    <TextField
      select
      label="Time to complete (optional)"
      value={values.dueAfterDays ?? ""}
      onChange={(event) => {
        const raw = event.target.value;
        onChange({
          ...values,
          dueAfterDays:
            raw === "" ? null : (Number(raw) as TaskProgramDueAfterDays),
        });
      }}
      helperText="How long trainees have to finish each training task after it is assigned."
      disabled={disabled}
      fullWidth
    >
      <MenuItem value="">No due date</MenuItem>
      {TASK_PROGRAM_DUE_AFTER_DAYS_OPTIONS.map((days) => (
        <MenuItem key={days} value={days}>
          {DUE_AFTER_DAYS_LABEL[days]}
        </MenuItem>
      ))}
    </TextField>

    <Box
      sx={{
        width: "fit-content",
        "& .MuiMenuItem-root": {
          gap: 0.5,
          justifyContent: "flex-start",
        },
      }}
    >
      <SwitchButton
        label="Enabled"
        checked={values.enabled}
        disabled={disabled}
        switchInputProps={{
          id: enabledInputId,
          "aria-label": "Enabled",
        }}
        onChange={(enabled) => onChange({ ...values, enabled })}
      />
    </Box>
  </Stack>
);

type TaskProgramAssignmentFieldsProps = {
  values: TaskProgramAssignmentValues;
  onChange: (values: TaskProgramAssignmentValues) => void;
  functionalGroups: FunctionalGroup[];
  orgUsers: OrgScopedBaseUser[];
  usersLoading?: boolean;
  disabled?: boolean;
};

export const TaskProgramAssignmentFields = ({
  values,
  onChange,
  functionalGroups,
  orgUsers,
  usersLoading = false,
  disabled = false,
}: TaskProgramAssignmentFieldsProps) => {
  const selectedFunctionalGroups = functionalGroups.filter((group) =>
    values.functionalGroupIds.includes(group.id)
  );
  const selectedUsers = orgUsers.filter((user) =>
    values.userIds.includes(user.id)
  );

  return (
    <Box className="flex flex-col gap-3">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Assign trainees
      </Typography>
      <Autocomplete
        multiple
        options={functionalGroups}
        getOptionLabel={(group) => `${group.name} (${group.acronym})`}
        value={selectedFunctionalGroups}
        onChange={(_, selected) =>
          onChange({
            ...values,
            functionalGroupIds: selected.map((group) => group.id),
          })
        }
        renderInput={(params) => (
          <TextField {...params} label="Functional groups" />
        )}
        sx={
          values.functionalGroupIds.length > 0
            ? SELECTED_AUTOCOMPLETE_SX
            : undefined
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={disabled}
      />

      <Autocomplete
        multiple
        options={orgUsers}
        getOptionLabel={(user) => getUserDisplayName(user)}
        value={selectedUsers}
        loading={usersLoading}
        onChange={(_, selected) =>
          onChange({
            ...values,
            userIds: selected.map((user) => user.id),
          })
        }
        renderInput={(params) => (
          <TextField {...params} label="Individual users" />
        )}
        sx={values.userIds.length > 0 ? SELECTED_AUTOCOMPLETE_SX : undefined}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={disabled}
      />
    </Box>
  );
};
